import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useActivityOverview } from '../hooks/useActivityOverview';
import { StatusStatCards } from '../components/activity-overview/StatusStatCards';
import { StatusFilter } from '../components/activity-overview/StatusFilter';
import { DomainBreakdown } from '../components/activity-overview/DomainBreakdown';
import { StageBreakdown } from '../components/activity-overview/StageBreakdown';
import { SkillBreakdown } from '../components/activity-overview/SkillBreakdown';
import { PreferenceBreakdown } from '../components/activity-overview/PreferenceBreakdown';
import { CoverageMatrix } from '../components/activity-overview/CoverageMatrix';
import type { ActivityOverviewStatus } from '../types/activity-overview';

type OverviewTab = 'breakdowns' | 'matrix';

const TABS: Array<{ value: OverviewTab; label: string }> = [
  { value: 'breakdowns', label: 'Breakdowns' },
  { value: 'matrix', label: 'Coverage Matrix' },
];

/**
 * Play Activities Overview — read-only catalogue picture: status counts,
 * breakdowns by domain / stage / skill / preference, and the stage × domain
 * coverage matrix exposing recommendation dead-zones.
 */
export default function ActivityOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: OverviewTab = searchParams.get('tab') === 'matrix' ? 'matrix' : 'breakdowns';
  const [status, setStatus] = useState<ActivityOverviewStatus>('published');
  const { data, isLoading, error } = useActivityOverview(status);

  const setTab = (next: OverviewTab) => {
    setSearchParams(next === 'breakdowns' ? {} : { tab: next });
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-pine-display font-display text-pine-ink mb-2">Overview</h1>
        <p className="text-body text-pine-ink-soft">
          Distribution of the play activity catalogue across domains, communication stages, domain
          skills and preferences — and where the gaps are.
        </p>
      </motion.div>

      {/* Header stat cards — always unfiltered */}
      <StatusStatCards counts={data?.statusCounts} isLoading={isLoading} />

      <StatusFilter value={status} onChange={setStatus} />

      {/* Tabs (URL state so they survive refresh and are linkable) */}
      <div className="inline-flex rounded-pine-input border border-pine-line bg-pine-card p-1 gap-1">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setTab(item.value)}
            className={cn(
              'px-4 py-2 rounded-pine-input text-sm font-medium transition-colors',
              tab === item.value
                ? 'bg-pine-active-item text-pine-pine'
                : 'text-pine-ink-soft hover:bg-pine-hover-item'
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? (
        <Card className="bg-pine-card border-pine-danger shadow-pine-card rounded-pine-card">
          <CardContent className="pt-6">
            <p className="text-pine-danger font-semibold">Failed to load the activity overview.</p>
            <p className="text-caption text-pine-ink-soft mt-1">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      ) : isLoading || !data ? (
        <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="h-10 bg-pine-line animate-pulse rounded" />
              <div className="h-10 bg-pine-line animate-pulse rounded" />
              <div className="h-10 bg-pine-line animate-pulse rounded" />
            </div>
          </CardContent>
        </Card>
      ) : tab === 'breakdowns' ? (
        <div className="space-y-6">
          <DomainBreakdown rows={data.breakdowns.domains} />
          <StageBreakdown rows={data.breakdowns.stages} coverage={data.breakdowns.stageCoverage} />
          <SkillBreakdown groups={data.breakdowns.domainSkills} />
          <PreferenceBreakdown rows={data.breakdowns.preferences} />
        </div>
      ) : (
        <CoverageMatrix
          rows={data.coverageMatrix.rows}
          columnTotals={data.coverageMatrix.columnTotals}
          grandTotal={data.coverageMatrix.grandTotal}
          domains={data.breakdowns.domains}
        />
      )}
    </div>
  );
}
